import {createContext, useContext, useMemo, type ReactNode} from "react";

interface ServerTableToolbarHostValue {
    /** Where the managed controls render once they are hoisted. */
    actions: HTMLElement | null
    /** A positioned box spanning the header, for search to take over while it is open. */
    searchOverlay: HTMLElement | null
}

/**
 * A page can offer its own header as a landing place for the table's managed
 * controls. The context carries elements rather than a layout so the server table
 * stays independent of whichever header renders it, and so a page can withhold the
 * host (desktop, or a screen where the header belongs to something else) simply by
 * providing `null`.
 */
const ServerTableToolbarHostContext = createContext<ServerTableToolbarHostValue>({
    actions: null,
    searchOverlay: null
});

interface ServerTableToolbarHostProviderProps {
    host: HTMLElement | null
    searchOverlayHost?: HTMLElement | null
    children: ReactNode
}

export const ServerTableToolbarHostProvider = ({
    host,
    searchOverlayHost = null,
    children
}: ServerTableToolbarHostProviderProps) => {
    const value = useMemo(
        () => ({actions: host, searchOverlay: searchOverlayHost}),
        [host, searchOverlayHost]
    );

    return (
        <ServerTableToolbarHostContext.Provider value={value}>
            {children}
        </ServerTableToolbarHostContext.Provider>
    );
};

export const useServerTableToolbarHost = () => useContext(ServerTableToolbarHostContext);
